<template>
  <div class="root">
    <sp-label v-if="label" block for="input">{{ label }}</sp-label>
    <sp-search-box
      id="input"
      v-model="liveQuery"
      clearable
      :density="inputDensity"
      :input-test-id="inputTestId"
      :placeholder="placeholder"
      @text-input="handleTextInput"
      @keydown.space.stop
      @keydown.enter.stop
    ></sp-search-box>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { GOOGLE_MAPS_LOADER, useGoogleMaps } from "./composables/useGoogleMaps";
import { props as propsDefinitions } from "./props.js";

const emit = defineEmits(["update-model-value", "update-search-query"]);

const props = defineProps(propsDefinitions);

// The field's v-model mirror. `searchQuery` stays the programmatic channel
// (preset, selection mirror, clear), mirrored in through the watcher.
const liveQuery = ref(props.searchQuery ?? "");
watch(
  () => props.searchQuery,
  (value) => (liveQuery.value = value ?? ""),
);

// Don't load Google Maps on mount - only load when user types
const mapsLoader = inject(GOOGLE_MAPS_LOADER, useGoogleMaps);
const { maps, loadGoogleMaps } = mapsLoader({ ...props, loadOnMount: false });

const predictions = ref(null);
const model = computed(() => predictions.value && sortedByRegion(predictions.value).map(predictionToModelEntry));
watch(model, () => emit("update-model-value", model.value));

const autocompleteService = ref();

// The most recent query the user has typed. Used to discard out-of-order
// prediction callbacks from stale requests — e.g. ones fired for earlier
// keystrokes that were queued while the Maps script was still loading.
let latestQuery = "";

function initializeAutocompleteService() {
  // Guard against double initialization
  if (autocompleteService.value) {
    return;
  }
  autocompleteService.value = new maps.value.places.AutocompleteService();
}

async function handleTextInput({ detail }) {
  const [query] = detail;

  liveQuery.value = query;
  emit("update-search-query", query);

  latestQuery = query;

  if (!query) {
    predictions.value = [];
    return;
  }

  // The composable loads the Maps script at most once, even when this fires on
  // every keystroke before it resolves. If the load fails, bail gracefully —
  // the loader resets its cached promise so a later keystroke can retry.
  try {
    await loadGoogleMaps();
  } catch {
    return;
  }

  // Bail if a newer keystroke (or a clear) superseded this query while waiting.
  if (latestQuery !== query) {
    return;
  }

  initializeAutocompleteService();
  searchPlaces(query);
}

const BOUNDS_CORNERS = ["sw_lat", "sw_lng", "ne_lat", "ne_lng"];

function predictionBounds() {
  const corners = BOUNDS_CORNERS.map((corner) => {
    const value = props.bounds?.[corner];
    return typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  });

  if (!corners.every(Number.isFinite)) {
    return undefined;
  }

  const { LatLng, LatLngBounds } = maps.value;

  return new LatLngBounds(new LatLng(corners[0], corners[1]), new LatLng(corners[2], corners[3]));
}

function searchPlaces(query) {
  const options = {
    input: query,
    fields: props.fields,
    types: props.types,
    componentRestrictions: props.componentRestrictions,
  };

  const bounds = predictionBounds();
  if (bounds) {
    options.bounds = bounds;
  }

  autocompleteService.value.getPlacePredictions(options, (predictions, status) =>
    handlePredictions(query, predictions, status),
  );
}

function handlePredictions(query, results, status) {
  // Ignore predictions for a query the user has already moved past.
  if (latestQuery !== query) {
    return;
  }

  const hasPredictions = status === maps.value.places.PlacesServiceStatus.OK;
  predictions.value = hasPredictions ? results : [];
}

function regionRank(prediction) {
  const region = (prediction.structured_formatting?.secondary_text ?? "").replace(/, USA$/, "");
  const rank = (props.sortOrder ?? []).indexOf(region);

  return rank < 0 ? Number.POSITIVE_INFINITY : rank;
}

function compareRegionRank(a, b) {
  const [rankA, rankB] = [regionRank(a), regionRank(b)];

  return rankA === rankB ? 0 : rankA - rankB;
}

function sortedByRegion(predictions) {
  return [...predictions].sort(compareRegionRank);
}

/**
 * Convert a prediction object from the Google Maps Places API to a model entry.
 *
 * @param prediction
 * @returns {{name: string, value: string, selectedText: string}}
 */
function predictionToModelEntry(prediction) {
  // Submit the full description so the geocoder gets the unambiguous value (Google returns US
  // places with a ", USA" suffix), but strip that suffix from the display name — the sites are
  // US-only, so ", USA" is redundant noise in the dropdown and the selected-location label.
  const value = prediction.description ?? "";
  const name = value.replace(/, USA$/, "");
  const selectedText = prediction.structured_formatting?.main_text ?? "";

  return { name, value, selectedText };
}
</script>

<style>
:host {
  display: block;
  box-sizing: border-box;
}
</style>

<style lang="scss" scoped>
sp-label {
  margin-bottom: var(--sp-comp-google-maps-auto-complete-label-margin-bottom, var(--sp-ref-scale-6));
}
</style>
